import * as React from "react";
import { Copy, Download, ExternalLink, Printer, Share2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useToast } from "@app/providers/toast";
import { copyTextToClipboard } from "@shared/lib/clipboard";
import { isFullyQualifiedHttpUrl } from "@shared/lib/storefront";
import { Button } from "./Button";
import { Dialog } from "./Dialog";
import { Input } from "./Input";

type StorefrontQrCodeDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  storeName?: string | null;
};

type ShareNavigator = Navigator & {
  canShare?: (data?: ShareData) => boolean;
  share?: (data?: ShareData) => Promise<void>;
};

function qrFileName(storeName?: string | null) {
  const normalized = String(storeName || "storefront")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);

  return `${normalized || "storefront"}-qr-code.png`;
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    if (typeof canvas.toBlob === "function") {
      canvas.toBlob(resolve, "image/png");
      return;
    }

    try {
      const [metadata, base64 = ""] = canvas.toDataURL("image/png").split(",");
      const mimeType = metadata.match(/data:([^;]+)/)?.[1] || "image/png";
      const bytes = window.atob(base64);
      const buffer = new Uint8Array(bytes.length);
      for (let index = 0; index < bytes.length; index += 1) {
        buffer[index] = bytes.codePointAt(index) ?? 0;
      }
      resolve(new Blob([buffer], { type: mimeType }));
    } catch {
      resolve(null);
    }
  });
}

function createPrintSheet({
  canvas,
  storeName,
  heading,
  instruction,
  url,
}: {
  canvas: HTMLCanvasElement;
  storeName: string;
  heading: string;
  instruction: string;
  url: string;
}) {
  let imageUrl = "";
  try {
    imageUrl = canvas.toDataURL("image/png");
  } catch {
    return false;
  }

  const frame = document.createElement("iframe");
  frame.title = heading;
  frame.setAttribute("aria-hidden", "true");
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  document.body.appendChild(frame);

  const printDocument = frame.contentDocument;
  const printWindow = frame.contentWindow;
  if (!printDocument || !printWindow) {
    frame.remove();
    return false;
  }

  printDocument.open();
  printDocument.write("<!doctype html><html><head></head><body></body></html>");
  printDocument.close();

  const style = printDocument.createElement("style");
  style.textContent = `
    @page { size: auto; margin: 16mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      min-height: 100vh;
      display: grid;
      place-items: center;
      color: #111827;
      background: #ffffff;
      font-family: Arial, Helvetica, sans-serif;
    }
    main {
      width: 112mm;
      max-width: 100%;
      padding: 12mm;
      border: 1px solid #d1d5db;
      border-radius: 6mm;
      text-align: center;
    }
    h1 { margin: 0; font-size: 24pt; line-height: 1.15; }
    .instruction { margin: 4mm 0 6mm; font-size: 14pt; color: #374151; }
    img { display: block; width: 70mm; height: 70mm; margin: 0 auto; image-rendering: pixelated; }
    .url { margin: 6mm 0 0; overflow-wrap: anywhere; font-size: 9pt; color: #4b5563; }
  `;
  printDocument.head.appendChild(style);

  const sheet = printDocument.createElement("main");
  const title = printDocument.createElement("h1");
  title.textContent = storeName || heading;
  const helper = printDocument.createElement("p");
  helper.className = "instruction";
  helper.textContent = instruction;
  const image = printDocument.createElement("img");
  image.alt = "";
  const address = printDocument.createElement("p");
  address.className = "url";
  address.textContent = url;
  sheet.append(title, helper, image, address);
  printDocument.body.appendChild(sheet);

  let printed = false;
  const print = () => {
    if (printed) return;
    printed = true;
    try {
      printWindow.focus();
      printWindow.print();
    } finally {
      window.setTimeout(() => frame.remove(), 1_000);
    }
  };
  image.addEventListener("load", () => window.setTimeout(print, 0), {
    once: true,
  });
  image.src = imageUrl;
  window.setTimeout(print, 250);
  return true;
}

export function StorefrontQrCodeDialog({
  open,
  onOpenChange,
  url,
  storeName,
}: StorefrontQrCodeDialogProps) {
  const { t: tUi } = useTranslation("ui");
  const toast = useToast();
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const [renderStatus, setRenderStatus] = React.useState<
    "idle" | "rendering" | "ready" | "error"
  >("idle");
  const validUrl = isFullyQualifiedHttpUrl(url) ? url.trim() : "";
  const renderFailed = renderStatus === "error";
  const renderReady = renderStatus === "ready";
  const heading = tUi("visitOurOnlineStore", {
    defaultValue: "Visit our online store",
  });
  const instruction = tUi("scanToOpenTheStore", {
    defaultValue: "Scan to open the store",
  });

  React.useEffect(() => {
    if (!open || !validUrl) {
      setRenderStatus("idle");
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;
    let active = true;
    setRenderStatus("rendering");

    void import("bwip-js")
      .then(({ qrcode }) => {
        if (!active) return;
        qrcode(canvas, {
          bcid: "qrcode",
          text: validUrl,
          scale: 8,
          padding: 4,
          eclevel: "M",
          barcolor: "111827",
          backgroundcolor: "FFFFFF",
        });
        if (active) setRenderStatus("ready");
      })
      .catch(() => {
        if (active) setRenderStatus("error");
      });

    return () => {
      active = false;
    };
  }, [open, validUrl]);

  async function copyUrl() {
    const copied = await copyTextToClipboard(validUrl);
    if (copied) {
      toast.success(
        tUi("copiedUrlToClipboard", {
          defaultValue: "Copied URL to clipboard",
        }),
      );
    } else {
      toast.error(
        tUi("unableToCopyTheLink", {
          defaultValue: "Unable to copy the link.",
        }),
      );
    }
  }

  function downloadQrCode() {
    const canvas = canvasRef.current;
    if (!canvas || !renderReady) return;

    try {
      const download = document.createElement("a");
      download.href = canvas.toDataURL("image/png");
      download.download = qrFileName(storeName);
      document.body.appendChild(download);
      download.click();
      download.remove();
      toast.success(
        tUi("storefrontQrCodeDownloaded", {
          defaultValue: "Storefront QR code downloaded.",
        }),
      );
    } catch {
      toast.error(tUi("downloadFailed", { defaultValue: "Download failed" }));
    }
  }

  function printQrCode() {
    const canvas = canvasRef.current;
    if (
      !canvas ||
      !renderReady ||
      !createPrintSheet({
        canvas,
        storeName: storeName?.trim() || heading,
        heading,
        instruction,
        url: validUrl,
      })
    ) {
      toast.error(
        tUi("couldNotPrepareStorefrontQrCode", {
          defaultValue: "Could not prepare the storefront QR code.",
        }),
      );
    }
  }

  async function shareQrCode() {
    const shareNavigator = navigator as ShareNavigator;
    const shareData: ShareData = {
      title: storeName?.trim() || heading,
      text: instruction,
      url: validUrl,
    };

    if (!shareNavigator.share) {
      await copyUrl();
      return;
    }

    try {
      const canvas = canvasRef.current;
      const blob = canvas && renderReady ? await canvasToPngBlob(canvas) : null;
      const file =
        blob && typeof File === "function"
          ? new File([blob], qrFileName(storeName), { type: "image/png" })
          : null;
      const fileShareData = file ? { ...shareData, files: [file] } : null;

      if (fileShareData && shareNavigator.canShare?.(fileShareData)) {
        await shareNavigator.share(fileShareData);
      } else {
        await shareNavigator.share(shareData);
      }
    } catch (cause) {
      if (cause instanceof DOMException && cause.name === "AbortError") return;
      toast.error(
        tUi("unableToOpenTheDeviceShareMenu", {
          defaultValue: "Unable to open the device share menu.",
        }),
      );
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={tUi("shareStorefrontQrCode", {
        defaultValue: "Share storefront QR code",
      })}
      description={tUi("storefrontQrCodeDescription", {
        defaultValue:
          "Customers can scan this code to open your store. Download it for messages and social posts, or print it for your physical store.",
      })}
      maxWidthClassName="max-w-xl"
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            {tUi("close", { defaultValue: "Close" })}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!validUrl || !renderReady}
            onClick={downloadQrCode}
          >
            <Download className="h-4 w-4" />
            {tUi("downloadPng", { defaultValue: "Download PNG" })}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={!validUrl || !renderReady}
            onClick={printQrCode}
          >
            <Printer className="h-4 w-4" />
            {tUi("print", { defaultValue: "Print" })}
          </Button>
          <Button
            type="button"
            disabled={!validUrl || !renderReady}
            onClick={() => void shareQrCode()}
          >
            <Share2 className="h-4 w-4" />
            {tUi("share", { defaultValue: "Share" })}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex justify-center rounded-2xl border bg-slate-100 p-3 sm:p-5">
          <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-5 text-center text-slate-900 shadow-sm sm:p-7">
            <h3 className="text-xl font-semibold text-slate-950">
              {storeName?.trim() || heading}
            </h3>
            <p className="mt-2 text-sm text-slate-600">{instruction}</p>
            {renderFailed ? (
              <div
                className="mx-auto mt-5 flex aspect-square w-[256px] max-w-full items-center justify-center rounded-xl border border-dashed border-red-300 bg-red-50 p-6 text-sm text-red-700"
                role="alert"
              >
                {tUi("couldNotPrepareStorefrontQrCode", {
                  defaultValue: "Could not prepare the storefront QR code.",
                })}
              </div>
            ) : (
              <canvas
                ref={canvasRef}
                role="img"
                aria-busy={renderStatus === "rendering" || undefined}
                aria-label={tUi("storefrontQrCode", {
                  defaultValue: "Storefront QR code",
                })}
                className="mx-auto mt-5 h-64 w-64 max-w-full"
              />
            )}
            <p className="mt-4 break-all text-xs text-slate-500">{validUrl}</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Input
            aria-label={tUi("storefrontUrlTitleCase", {
              defaultValue: "Storefront URL",
            })}
            value={validUrl}
            readOnly
            className="min-w-0 font-mono text-xs"
          />
          <Button
            type="button"
            size="icon"
            variant="outline"
            disabled={!validUrl}
            onClick={() => void copyUrl()}
            aria-label={tUi("copyUrl", { defaultValue: "Copy URL" })}
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            disabled={!validUrl}
            onClick={() =>
              window.open(validUrl, "_blank", "noopener,noreferrer")
            }
            aria-label={tUi("openStore", { defaultValue: "Open Store" })}
          >
            <ExternalLink className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
